import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../../utils/common/cn";
import { Plus, Minus } from "lucide-react";
import React, { useState, useEffect } from "react";

export interface NavItem {
  path?: string;
  label: string;
  children?: NavItem[];
  /** State mode + nextBlocked only: this item stays enabled/clickable no matter where the
   *  viewer currently is in the sequence, ignoring both the completed/future check and
   *  nextBlocked — for an item that isn't really a sequential step (e.g. a status page you
   *  should always be able to jump to) but still lives in the same trail. */
  alwaysEnabled?: boolean;
}

export interface StepsNavigationProps {
  /** Array of navigation items with path and label */
  navItems: NavItem[];
  /** Base path for route matching (required in route mode) */
  basePath?: string;
  /** Current flat step index (0-based, traverses parent→children→next parent) */
  currentStepIndex?: number;
  /** Callback to navigate to a step by its flat index */
  onStepChange?: (index: number) => void;
  /**
   * Controls whether future steps are blocked.
   * - true: future steps are disabled
   * - false: future steps are clickable
   */
  nextBlocked?: boolean;
  /** Additional className */
  className?: string;
  /**
   * true (default): renders as the one product-wide fixed top bar, positioned below the
   * header via the top-[110px] offset this was originally built for.
   * false: renders in normal document flow instead — for a second, narrower instance of this
   * same step trail embedded inside a page's own content (e.g. a wizard showing its own
   * sub-steps), where a second fixed bar would stack on top of the product-wide one.
   */
  fixed?: boolean;
}

export function StepsNavigation({
  navItems,
  basePath,
  currentStepIndex,
  onStepChange,
  nextBlocked = false,
  className,
  fixed = true,
}: StepsNavigationProps) {
  const location = useLocation();
  const useStateNavigation =
    currentStepIndex !== undefined && typeof onStepChange === "function";
  const safeCurrentStepIndex = currentStepIndex ?? 0;

  // Track which parent items are expanded
  const [expandedParents, setExpandedParents] = useState<Set<number>>(new Set());

  // Build flat-index ranges per parent: flatRanges[parentIdx] = { start, end }
  // e.g. parent with 2 children: start=0 (parent itself), end=2 (last child)
  const flatRanges: { start: number; end: number }[] = [];
  const flattenedItems: NavItem[] = [];
  let flatIdx = 0;
  navItems.forEach((item) => {
    const start = flatIdx;
    flattenedItems.push(item);
    flatIdx++; // parent itself
    if (item.children) {
      item.children.forEach((child) => flattenedItems.push(child));
      flatIdx += item.children.length;
    }
    flatRanges.push({ start, end: flatIdx - 1 });
  });

  const pathMatches = (path?: string) => {
    if (!path) return false;
    if (basePath && path === basePath) {
      return (
        location.pathname === basePath || location.pathname === `${basePath}/`
      );
    }
    return (
      location.pathname === path ||
      location.pathname === `${path}/` ||
      location.pathname.startsWith(path)
    );
  };

  const currentFlatIndex = useStateNavigation
    ? safeCurrentStepIndex
    : Math.max(flattenedItems.findIndex((item) => pathMatches(item.path)), 0);

  // Determine which parent is active and which child within it
  const getActiveInfo = () => {
    if (!useStateNavigation) {
      for (let i = 0; i < navItems.length; i++) {
        const parent = navItems[i];
        const childIdx = parent.children?.findIndex((child) =>
          pathMatches(child.path)
        );
        if ((childIdx ?? -1) >= 0) {
          return { parentIndex: i, childIndex: childIdx as number };
        }
        if (pathMatches(parent.path)) {
          return { parentIndex: i, childIndex: -1 };
        }
      }
    }

    for (let i = 0; i < navItems.length; i++) {
      if (
        currentFlatIndex >= flatRanges[i].start &&
        currentFlatIndex <= flatRanges[i].end
      ) {
        const childOffset = currentFlatIndex - flatRanges[i].start - 1;
        return { parentIndex: i, childIndex: childOffset >= 0 ? childOffset : -1 };
      }
    }
    return { parentIndex: 0, childIndex: -1 };
  };

  const { parentIndex: activeParentIndex, childIndex: activeChildIndex } = getActiveInfo();

  const isParentActive = (parentIdx: number) => activeParentIndex === parentIdx;

  const isParentCompleted = (parentIdx: number) =>
    currentFlatIndex > flatRanges[parentIdx].end;

  // Auto-expand parent when a child within it is active
  useEffect(() => {
    if (activeChildIndex >= 0) {
      setExpandedParents(prev => {
        const next = new Set(prev);
        next.add(activeParentIndex);
        return next;
      });
    }
  }, [activeParentIndex, activeChildIndex]);

  const toggleExpand = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <nav
      className={cn(
        "py-3 px-6 overflow-x-auto",
        fixed ? "fixed top-[110px] left-0 z-40" : "relative",
        className
      )}
      style={
        fixed
          ? { backgroundColor: "#191e27", right: "var(--rightNavWidth)", transition: "right 0.2s ease" }
          : { backgroundColor: "#191e27" }
      }
    >
      <div className="flex items-center gap-1 min-w-max">
        {navItems.map((item, index) => {
          const isActive = isParentActive(index);
          const isCompleted = isParentCompleted(index);
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedParents.has(index);
          // alwaysEnabled ignores nextBlocked entirely for this one item — it isn't a
          // sequential step, so "have you reached it yet" doesn't apply to it.
          const itemNextBlocked = nextBlocked && !item.alwaysEnabled;

          // A parent is clickable if:
          //  - it's completed (go back to that step), OR
          //  - it's the active parent but user is on a child (go back to parent step)
          //  - it's a future step and nextBlocked (for this item) is false
          const isFutureStep = !isActive && !isCompleted;
          const isOnChildOfThisParent = isActive && activeChildIndex >= 0;
          const parentClickable =
            useStateNavigation &&
            (isCompleted ||
              isOnChildOfThisParent ||
              (!itemNextBlocked && isFutureStep)) &&
            !!onStepChange;

          const handleParentClick = () => {
            if (parentClickable) {
              onStepChange!(flatRanges[index].start);
            }
          };

          return (
            <div key={item.label} className="flex items-center">
              {/* Parent step item — clickable only if completed */}
              <div
                className={cn(
                  "flex items-center gap-0.5 px-3 py-2 text-xs font-medium transition-colors",
                  isActive && "text-primary bg-primary/10",
                  isCompleted && "text-foreground",
                  isFutureStep &&
                    (itemNextBlocked
                      ? "text-muted-foreground opacity-50 cursor-not-allowed"
                      : "text-foreground")
                )}
              >
                {useStateNavigation ? (
                  <div
                    role={parentClickable ? "button" : undefined}
                    tabIndex={parentClickable ? 0 : undefined}
                    onClick={handleParentClick}
                    onKeyDown={
                      parentClickable
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") handleParentClick();
                          }
                        : undefined
                    }
                    className={cn(
                      "flex items-center gap-2 transition-colors",
                      isActive && !isOnChildOfThisParent && "text-primary",
                      isActive &&
                        isOnChildOfThisParent &&
                        "text-foreground cursor-pointer hover:text-primary",
                      isCompleted && "text-foreground cursor-pointer hover:text-primary",
                      isFutureStep &&
                        (itemNextBlocked
                          ? "text-muted-foreground opacity-50 cursor-not-allowed"
                          : "text-foreground cursor-pointer hover:text-primary")
                    )}
                  >
                    <span
                      className={cn(
                        "w-5 h-5 flex items-center justify-center text-[10px] border",
                        isActive && "bg-primary border-primary text-primary-foreground",
                        isCompleted && "bg-success/20 border-success text-success",
                        !isActive && !isCompleted && "border-muted-foreground/50"
                      )}
                    >
                      {index + 1}
                    </span>
                    <span className="hidden lg:inline">{item.label}</span>
                  </div>
                ) : (
                  <NavLink
                    to={item.path || basePath || "/promotioniq"}
                    className={cn(
                      "flex items-center gap-2 transition-colors",
                      isActive && "text-primary",
                      isCompleted && "text-foreground",
                      !isActive && !isCompleted && "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "w-5 h-5 flex items-center justify-center text-[10px] border",
                        isActive && "bg-primary border-primary text-primary-foreground",
                        isCompleted && "bg-success/20 border-success text-success",
                        !isActive && !isCompleted && "border-muted-foreground/50"
                      )}
                    >
                      {index + 1}
                    </span>
                    <span className="hidden lg:inline">{item.label}</span>
                  </NavLink>
                )}

                {/* Expand/Collapse toggle */}
                {hasChildren && (
                  <button
                    onClick={(e) => toggleExpand(index, e)}
                    className={cn(
                      "w-4 h-4 flex items-center justify-center border text-[10px] ml-0.5 transition-colors cursor-pointer",
                      isActive
                        ? "border-primary text-primary hover:bg-primary/20"
                        : "border-muted-foreground/50 text-muted-foreground hover:border-primary hover:text-primary"
                    )}
                  >
                    {isExpanded ? <Minus className="w-2.5 h-2.5" /> : <Plus className="w-2.5 h-2.5" />}
                  </button>
                )}
              </div>

              {/* Children items — clickable if completed, shown when expanded */}
              {hasChildren && isExpanded && item.children!.map((child, childIndex) => {
                const childIsActive = isActive && activeChildIndex === childIndex;
                const childFlatIdx = flatRanges[index].start + 1 + childIndex;
                const childIsPrevious = currentFlatIndex > childFlatIdx;
                const childIsFuture = !childIsActive && !childIsPrevious;
                const childNextBlocked = nextBlocked && !child.alwaysEnabled;
                const childClickable =
                  useStateNavigation &&
                  (childIsPrevious || (!childNextBlocked && childIsFuture)) &&
                  !!onStepChange;

                const handleChildClick = () => {
                  if (childClickable) {
                    onStepChange!(childFlatIdx);
                  }
                };

                return (
                  <React.Fragment key={child.label}>
                    {/* Square indicator for sub-steps */}
                    <div className={cn(
                      "w-1.5 h-1.5 mx-1.5",
                      childIsPrevious || childIsActive ? "bg-primary" : "bg-muted-foreground/50"
                    )} />
                    {useStateNavigation ? (
                      <div
                        role={childClickable ? "button" : undefined}
                        tabIndex={childClickable ? 0 : undefined}
                        onClick={handleChildClick}
                        onKeyDown={
                          childClickable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") handleChildClick();
                              }
                            : undefined
                        }
                        className={cn(
                          "flex items-center gap-1.5 px-2 py-2 text-xs font-medium transition-colors",
                          childIsActive && "text-primary bg-primary/10",
                          childIsPrevious &&
                            "text-foreground cursor-pointer hover:text-primary",
                          childIsFuture &&
                            (childNextBlocked
                              ? "text-muted-foreground opacity-50 cursor-not-allowed"
                              : "text-foreground cursor-pointer hover:text-primary")
                        )}
                      >
                        <span
                          className={cn(
                            "w-4 h-4 flex items-center justify-center text-[9px] border",
                            childIsActive &&
                              "bg-primary border-primary text-primary-foreground",
                            childIsPrevious &&
                              "bg-success/20 border-success text-success",
                            !childIsActive &&
                              !childIsPrevious &&
                              "border-muted-foreground/50"
                          )}
                        >
                          {String.fromCharCode(97 + childIndex)}
                        </span>
                        <span className="hidden lg:inline">{child.label}</span>
                      </div>
                    ) : (
                      <NavLink
                        to={child.path || item.path || basePath || "/promotioniq"}
                        className={cn(
                          "flex items-center gap-1.5 px-2 py-2 text-xs font-medium transition-colors",
                          childIsActive && "text-primary bg-primary/10",
                          childIsPrevious && "text-foreground",
                          !childIsActive &&
                            !childIsPrevious &&
                            "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <span
                          className={cn(
                            "w-4 h-4 flex items-center justify-center text-[9px] border",
                            childIsActive &&
                              "bg-primary border-primary text-primary-foreground",
                            childIsPrevious &&
                              "bg-success/20 border-success text-success",
                            !childIsActive &&
                              !childIsPrevious &&
                              "border-muted-foreground/50"
                          )}
                        >
                          {String.fromCharCode(97 + childIndex)}
                        </span>
                        <span className="hidden lg:inline">{child.label}</span>
                      </NavLink>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Connector line to next parent */}
              {index < navItems.length - 1 && (
                <div
                  className={cn(
                    "w-4 h-px",
                    isCompleted ? "bg-primary" : "bg-border"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

export default StepsNavigation;
